import { useEffect } from 'react';
import { ShieldCheck, Lock } from 'lucide-react';
import { formatMoney } from '../../lib/format.js';
import { postReady } from '../../checkout/embedBridge.js';

/**
 * HC-1: the merchant's logo, display name, accent colour and support email.
 * HC-2: no provider name, logo, badge or redirect appears anywhere.
 * HC-14: works on a phone, and states plainly that card details go straight to
 *        the payment network.
 *
 * Layout follows the convention customers already know from a hosted checkout:
 * a summary rail on the left, the form on the right, collapsing to one column
 * on a phone with the amount pinned at the top.
 *
 * The page is deliberately pinned to the light theme. A merchant picks an accent
 * colour looking at a white page; rendering it against near-black could make
 * their brand illegible, and this is the one screen where their brand has to be
 * right.
 */
export function CheckoutShell({ merchant, payment, children, summary, showTrust = true, bare = false }) {
  const accent = /^#[0-9a-fA-F]{6}$/.test(merchant?.accentColor ?? '') ? merchant.accentColor : '#386db2';
  const name = merchant?.displayName || 'Checkout';

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    // Lets a merchant's page drop its own spinner once we have painted. A no-op
    // unless this checkout is running inside their site as a drop-in.
    postReady();
  }, []);

  /**
   * `bare` drops the summary rail and gives the step frame the whole panel.
   *
   * The rail repeats the merchant and the amount, which the stepped header
   * already pins at the top — and inside a drop-in modal on a phone it pushed
   * the actual form below the fold, which is the scrolling the two-column
   * layout was supposed to prevent.
   */
  if (bare) {
    return (
      <div
        className="theme-light mx-auto min-h-screen w-full max-w-lg bg-surface"
        style={{ '--accent-brand': accent }}
      >
        {children}
      </div>
    );
  }

  return (
    <div className="theme-light min-h-screen bg-surface-2" style={{ '--accent-brand': accent }}>
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col lg:flex-row">
        {/* ------------------------------ summary rail ----------------------------- */}
        <aside
          className="border-b px-5 py-6 sm:px-8 lg:w-[44%] lg:border-b-0 lg:border-r lg:px-10 lg:py-12"
          style={{ borderColor: 'rgb(var(--line))' }}
        >
          <div className="mx-auto max-w-sm lg:mx-0 lg:ml-auto lg:max-w-xs">
            <div className="flex items-center gap-2.5">
              {merchant?.logoUrl ? (
                <img
                  src={merchant.logoUrl}
                  alt={name}
                  className="h-8 w-8 rounded-lg object-contain"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span
                  className="grid h-8 w-8 place-items-center rounded-lg text-sm font-semibold text-white"
                  style={{ background: accent }}
                  aria-hidden="true"
                >
                  {name.charAt(0).toUpperCase()}
                </span>
              )}
              <span className="truncate text-sm font-medium text-ink-soft">{name}</span>
            </div>

            {payment && (
              <div className="mt-7">
                <p className="eyebrow">{payment.description ? 'Paying for' : 'Amount due'}</p>
                {payment.description && (
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{payment.description}</p>
                )}
                <p className="tabular mt-2 text-[34px] font-semibold leading-none tracking-tight text-ink">
                  {formatMoney(payment.amount, payment.currency)}
                </p>
                {payment.orderId && (
                  <p className="mt-2.5 font-mono text-xs text-muted">{payment.orderId}</p>
                )}
              </div>
            )}

            {summary}

            {showTrust && (
              <ul className="mt-8 space-y-2.5 border-t pt-6" style={{ borderColor: 'rgb(var(--line))' }}>
                {[
                  [Lock, 'Your card details are encrypted in your browser.'],
                  [ShieldCheck, 'They go straight to the payment network — never through this site.'],
                ].map(([Icon, text]) => (
                  <li key={text} className="flex items-start gap-2.5 text-xs leading-relaxed text-muted">
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden="true" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            )}

            {/*
              * The merchant's brand owns this page (HC-2 keeps the *processor*
              * out of it entirely). A quiet platform mark is the convention on a
              * hosted checkout — it tells a customer who is holding their card
              * details, which is reassurance, not advertising.
              */}
            <div className="mt-7 flex items-center gap-2 border-t pt-5" style={{ borderColor: 'rgb(var(--line))' }}>
              <img
                src="/distrx-icon.gif"
                alt=""
                aria-hidden="true"
                width={18}
                height={18}
                draggable={false}
                className="shrink-0 select-none rounded"
                style={{ border: '1px solid rgb(var(--line))', background: '#fff' }}
              />
              <span className="text-[11px] text-faint">
                Powered by <span className="font-medium text-muted">DistrxPay</span>
              </span>
            </div>

            {merchant?.supportEmail && (
              <p className="mt-4 text-xs text-muted">
                Questions?{' '}
                <a
                  href={`mailto:${merchant.supportEmail}`}
                  className="font-medium text-ink-soft underline decoration-line underline-offset-2 hover:text-ink"
                >
                  {merchant.supportEmail}
                </a>
              </p>
            )}
          </div>
        </aside>

        {/* --------------------------------- form ---------------------------------- */}
        <main className="flex-1 px-5 py-7 sm:px-8 lg:px-10 lg:py-12">
          <div className="mx-auto max-w-sm lg:mx-0 lg:max-w-md">{children}</div>
        </main>
      </div>
    </div>
  );
}

export default CheckoutShell;
